import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid2";
import { Stack, Button, Container } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { setContainer } from "@/store/uiSlice";
import PageHeader from "@/components/PageHeader";
import PageLoading from "@/components/page-loading/PageLoading";
import useSnackbar from "@/hooks/useSnackbar";
import {
  useFetchOrganizationWebsiteByIdQuery,
  useUpdateOrganizationMutation,
} from "@/features/organizations/api/organizationsApi";
import { useFetchTemplatesByIndustryQuery } from "@/api/templatesApi";
import {
  useUpdateWebsiteMutation,
  useCreateOrganizationAliasMutation,
} from "@/features/organizations/api/websiteApi";
import { skipToken } from "@reduxjs/toolkit/query";

import VisualIdentitySection from "./components/VisualIdentitySection";
import HeroSection from "./components/HeroSection";
import SocialMediaSection from "./components/SocialMediaSection";
import QuickLinksSection from "./components/QuickLinksSection";
import SeoMetadataSection from "./components/SeoMetadataSection";
import PreviewMockup from "./components/PreviewMockup";
import DesignSection from "./components/DesignSection";

export const MiSitioSchema = Yup.object().shape({
  metaData: Yup.object().shape({
    title: Yup.string()
      .min(2, "El título debe tener al menos 2 caracteres")
      .max(60, "El título no puede tener más de 60 caracteres"),
    description: Yup.string()
      .min(2, "La descripción debe tener al menos 2 caracteres")
      .max(160, "La descripción no puede tener más de 160 caracteres"),
  }),
  organization: Yup.object().shape({
    alias: Yup.string().required("La URL de tu sitio es obligatoria"),
    primaryColor: Yup.string().required("El color de marca es obligatorio"),
    facebook: Yup.string().nullable(),
    instagram: Yup.string().nullable(),
    tiktok: Yup.string().nullable(),
  }),
  template: Yup.string().required("La plantilla es obligatoria"),
});

const mergeSettings = (newSettings: any, currentSettings: any) => {
  if (!newSettings) return currentSettings;
  const merged = { ...newSettings };

  // Create a mapping of lowercase key -> original setting object
  const currentLowerMap = new Map<string, any>();
  if (currentSettings) {
    for (const [key, val] of Object.entries(currentSettings)) {
      currentLowerMap.set(key.toLowerCase(), val);
    }
  }

  for (const key of Object.keys(merged)) {
    const lowerKey = key.toLowerCase();
    const currentSetting = currentLowerMap.get(lowerKey);
    const currentVal = currentSetting?.value;

    if (currentVal !== undefined && currentVal !== "") {
      merged[key] = {
        ...merged[key],
        value: currentVal
      };
    }
  }
  return merged;
};

const TemplateSettingsSync = ({
  selectedTemplateId,
  templateSettings,
}: {
  selectedTemplateId: string;
  templateSettings?: any;
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const isFirstMountRef = useRef<boolean>(true);
  const lastSyncedIdRef = useRef<string>("");

  useEffect(() => {
    if (selectedTemplateId && templateSettings) {
      if (isFirstMountRef.current) {
        isFirstMountRef.current = false;
        lastSyncedIdRef.current = selectedTemplateId;
        return;
      }

      if (selectedTemplateId !== lastSyncedIdRef.current) {
        lastSyncedIdRef.current = selectedTemplateId;
        const merged = mergeSettings(templateSettings, values.settings);
        setFieldValue("settings", merged);
      }
    }
  }, [selectedTemplateId, templateSettings, setFieldValue, values.settings]);

  return null;
};

const MiSitioPage = () => {
  const dispatch = useAppDispatch();
  const container = useAppSelector((state) => state.ui?.container);
  const [openSnackbar] = useSnackbar();
  const { organizationId } = useParams<{ organizationId: string }>();
  const org = useAppSelector((state: any) =>
    organizationId ? state.organizations[organizationId] : null
  ) as any;
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const [updateWebsite] = useUpdateWebsiteMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const [createAlias] = useCreateOrganizationAliasMutation();
  const templatesByIndustryQuery = useFetchTemplatesByIndustryQuery(org?.industry || skipToken);

  // Set container layout to fluid on mount and restore to fixed on unmount
  useEffect(() => {
    dispatch(setContainer("fluid"));
    return () => {
      dispatch(setContainer("fixed"));
    };
  }, [dispatch]);

  const websiteQuery = useFetchOrganizationWebsiteByIdQuery(
    org?.website && organizationId
      ? { organizationId, websiteId: org.website }
      : skipToken
  );

  useEffect(() => {
    // Only set initial template for the first time
    if (websiteQuery?.data?.template && selectedTemplateId === "") {
      setSelectedTemplateId(websiteQuery.data.template);
    }
  }, [websiteQuery?.data?.template, selectedTemplateId]);

  const activeTemplate = useMemo(() => {
    if (templatesByIndustryQuery?.data?.length) {
      const found = templatesByIndustryQuery.data.find((t) => t.id === selectedTemplateId);
      if (found) return found;
    }
    return null;
  }, [selectedTemplateId, templatesByIndustryQuery?.data]);

  const initialState = useMemo(() => {
    if (!org) return undefined;

    const initialValues = {
      metaData: {
        title: websiteQuery?.data?.metaData?.title ?? "",
        description: websiteQuery?.data?.metaData?.description ?? "",
      },
      organization: {
        title: org?.title ?? "",
        slogan: org?.slogan ?? "",
        alias: org?.alias ?? "",
        primaryColor: org?.primaryColor ?? "#0058bc",
        facebook: org?.facebook ?? "",
        instagram: org?.instagram ?? "",
        tiktok: org?.tiktok ?? "",
      },
      settings: websiteQuery?.data?.settings ?? activeTemplate?.settings ?? {},
      template: websiteQuery?.data?.template ?? "",
    };

    if (websiteQuery?.data) return initialValues;
  }, [org, websiteQuery?.data, activeTemplate?.settings]);

  const handleSubmit = async (values: any) => {
    if (!organizationId || !websiteQuery?.data) return;

    try {
      // 1. Update Website details (metaData, settings, template)
      await updateWebsite({
        organizationId,
        websiteId: websiteQuery.data.id,
        website: {
          metaData: values.metaData,
          settings: values.settings,
          template: values.template,
        },
      });

      // 2. Update Organization details (alias, brand color, title, slogan)
      const orgPayload: any = {};
      if (values.organization.primaryColor !== org?.primaryColor) {
        orgPayload.primaryColor = values.organization.primaryColor;
      }
      if (values.organization.title !== org?.title) {
        orgPayload.title = values.organization.title;
      }
      if (values.organization.slogan !== org?.slogan) {
        orgPayload.slogan = values.organization.slogan;
      }
      if (values.organization.facebook !== org?.facebook) {
        orgPayload.facebook = values.organization.facebook;
      }
      if (values.organization.instagram !== org?.instagram) {
        orgPayload.instagram = values.organization.instagram;
      }
      if (values.organization.tiktok !== org?.tiktok) {
        orgPayload.tiktok = values.organization.tiktok;
      }

      if (values.organization.alias !== org?.alias && values.organization.alias) {
        console.log("Creating alias:", values.organization.alias);
        // Register the new alias in aliases collection
        await createAlias({
          alias: values.organization.alias,
          organizationId,
        }).unwrap();
        console.log("Alias created successfully!");
        orgPayload.alias = values.organization.alias;
      }

      if (Object.keys(orgPayload).length > 0) {
        console.log("Updating organization with payload:", orgPayload);
        await updateOrganization({
          organizationId,
          payload: orgPayload,
        }).unwrap();
        console.log("Organization updated successfully!");
      }

      openSnackbar("Sitio web actualizado exitosamente", "success");
    } catch (error) {
      openSnackbar("Error al guardar los cambios", "error");
      console.error("Error during save operation:", error);
    }
  };

  if (websiteQuery.isLoading || templatesByIndustryQuery.isLoading) {
    return <PageLoading />;
  }

  if (!initialState) {
    return null;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialState}
      validationSchema={MiSitioSchema}
      onSubmit={handleSubmit}
    >
      {({ submitForm, setFieldValue }) => (
        <Form style={{ height: "100vh" }}>
          <TemplateSettingsSync
            selectedTemplateId={selectedTemplateId}
            templateSettings={activeTemplate?.settings}
          />
          <Stack spacing={3} border={3} height="100vh" overflow={"auto"}>
            <PageHeader
              title="Mi sitio web"
              action={
                <Stack direction="row" spacing={2}>
                  <button
                    type="submit"
                    style={{ display: "none" }}
                    id="misitio-hidden-submit"
                  />
                  <Button
                    size="large"
                    color="success"
                    variant="contained"
                    onClick={submitForm}
                  >
                    Guardar
                  </Button>
                </Stack>
              }
            />

            <Stack pb={4}>
              <Container
                maxWidth={container === 'fixed' ? 'lg' : false}
                sx={{
                  height: '100%',
                }}
              >

                <Grid container spacing={3} flex={1}>
                  {/* Left column: configurations */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Stack spacing={2}>
                      <DesignSection
                        templates={templatesByIndustryQuery?.data ?? []}
                        isLoading={templatesByIndustryQuery?.isLoading}
                        selectedTemplateId={selectedTemplateId}
                        onSelect={(id) => {
                          setSelectedTemplateId(id);
                          setFieldValue("template", id);
                        }}
                      />
                      <VisualIdentitySection />
                      <HeroSection />
                      <SocialMediaSection />
                      <QuickLinksSection />
                      <SeoMetadataSection />
                    </Stack>
                  </Grid>

                  {/* Right column: preview mockup */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <PreviewMockup
                      website={websiteQuery.data}
                      activeTemplate={activeTemplate}
                    />
                  </Grid>
                </Grid>
              </Container>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default MiSitioPage;
